import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from "@mui/material";
import { startCase } from "lodash";
import TableComponent from "./TableComponent";
import EditDeliveryModal from "./ModalComponent";
import { useCallback, useState } from "react";
import { Cog, Download, Plus } from "lucide-react";
import { fetchDeliveriesAtom, fetchDeliveriesDownloadAtom } from "../../../atoms/logistics/LogisticsManagementAtom";
import { useAtom } from "jotai";
import DeleteDialog from "./DeleteModal";
import SetupDeliveryDialog from "./SetupDeliveryComponent";
import settingsAtom from "../../../atoms/settings/SettingsAtom";
import * as XLSX from 'xlsx-js-style';

const headers = [
  { key: 'date_time', label: 'Delivery Date' },
  { key: 'name', label: 'Delivery Name' },
  { key: 'comments', label: 'Comments' },
  { key: 'delivery_laydown_id', label: 'Delivery Laydown Name' },
  { key: 'bom_id', label: 'Part Description' },
  { key: 'part_number', label: 'Part Number' },
  { key: 'count', label: 'Delivered Qty' }, 
  { key: 'damaged', label: 'Damaged Qty' },
];

const DeliveryManagement = (props) => {
  const { loader, data, title, partList, laydownList, aliasName, customFields, setFields, downLoadData, current } = props;
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [, fetchDeliveries] = useAtom(fetchDeliveriesAtom);
  const [, fetchDownloadDeliveries] = useAtom(fetchDeliveriesDownloadAtom)
  const [visible, setVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(false);
  const [text, setText] = useState('');
  const [temp, setTemp] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
});
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")))
  const [loading, setLoading] = useState(false);


  const onClose = () => setTemp(false)


  const exportToExcel = (deliveryData, projectInfo = {}, headers, laydown, partList) => {  
  
    const flattenedData = [];
    
    deliveryData.forEach(delivery => {
      if (delivery.bom_delivery_laydowns && delivery.bom_delivery_laydowns.length > 0) {
        delivery.bom_delivery_laydowns.forEach((bomLaydown, index) => {
          const deliveryLaydown = delivery.delivery_laydowns.find(
            dl => dl.id === bomLaydown.delivery_laydown_id
          );
          
          flattenedData.push({
            date_time: index === 0 ? new Date(delivery.date_time * 1000).toLocaleDateString() : '', 
            name: index === 0 ? delivery.name : '',
            comments: index === 0 ? delivery.comments : '', 
            delivery_laydown_id: deliveryLaydown ? laydown.find((item) => item.id === deliveryLaydown.laydown_id)?.name : '',
            bom_id: partList?.find((item) => item?.bom_id === bomLaydown.bom_id)?.description, 
            part_number: partList?.find((item) => item?.bom_id === bomLaydown.bom_id)?.part_name, 
            count: bomLaydown.count,
            damaged: bomLaydown.damaged,
          });
        });
      } else {
        flattenedData.push({
          date_time: new Date(delivery.date_time * 1000).toLocaleDateString(),
          name: delivery.name,
          comments: delivery.comments,
          delivery_laydown_id: '',
          bom_id: '',
          part_number: '',
          count: '',
          damaged: '',
        });
      }
    });
  
    const wb = XLSX.utils.book_new();
    
    const projectRows = [
      ['Project Number', projectInfo.projectNumber || '123'],
      ['Project Name', projectInfo.projectName || 'NX 21-05-2025'],
      ['Report', projectInfo.report || 'Laydown 2'],
      ['Report Date', projectInfo.reportDate || new Date().toLocaleDateString()],
      [], 
    ];
  
    const headerRow = headers.map(h => h.label);
    
    const dataRows = flattenedData.map(row => 
      headers.map(header => row[header.key] || '')
    );
  
    const allRows = [...projectRows, headerRow, ...dataRows];
  
    const ws = XLSX.utils.aoa_to_sheet(allRows);
  
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } }, 
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
  
    const projectInfoStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "E7E6E6" } }, 
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
  
    const dataStyle = {
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
  
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 2; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = { v: "" };
        ws[cellAddress].s = projectInfoStyle;
      }
    }
  
    const headerRowIndex = 5;
    for (let col = 0; col < headers.length; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });
      if (!ws[cellAddress]) ws[cellAddress] = { v: "" };
      ws[cellAddress].s = headerStyle;
    }
  
    for (let row = headerRowIndex + 1; row < allRows.length; row++) {
      for (let col = 0; col < headers.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = { v: "" };
        ws[cellAddress].s = dataStyle;
      }
    }
  
    const colWidths = [
      { wch: 15 }, // Delivery Date
      { wch: 20 }, // Delivery Name
      { wch: 15 }, // Comments
      { wch: 20 }, // Delivery Laydown Name
      { wch: 40 }, // Part Description
      { wch: 15 }, // Part Number
      { wch: 12 }, // Delivered Qty
      { wch: 12 }, // Damaged Qty
    ];
    ws['!cols'] = colWidths;
  
    XLSX.utils.book_append_sheet(wb, ws, 'Delivery Report');
  
    const fileName = `Delivery_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };


  const defaultFormValues = [
    {
       label: "Delivery Date",
       key: 1,
       value: 4
    },
    {
        label: "Delivery Time",
        key: 2,
        value: 5
     },
     {
        label: "Name",
        key: 3,
        value: 2
     },
     {
        label: "Comments",
        key: 4,
        value: 6
     },
     {
        label: "Documents",
        key: 5, 
        value: 7
     }

  ]

  const handleFetch = async () => {
    await fetchDeliveries()
    await fetchDownloadDeliveries()
  }

  const handleEdit = (row) => {
    setEditData(row);  
    setOpen(true);
  };

  const handleDelete = (value) => {
    setSelectedId(value?.id)
    setText(value?.name)
    setVisible(true)
  }

  const result = (() => {
    let fieldsArray = [];
    
    if (customFields?.custom_inputs && Array.isArray(customFields.custom_inputs)) {
      fieldsArray = customFields.custom_inputs;
    } else if (Array.isArray(customFields)) {
      fieldsArray = customFields
        .map(item => Array.isArray(item) ? item[0] : item)
        .filter(field => field && field.name);
    } else if (customFields?.custom_response && Array.isArray(customFields.custom_response)) {
      fieldsArray = customFields.custom_response
        .map(item => Array.isArray(item) ? item[0] : item)
        .filter(field => field && field.name);
    }
    
    return fieldsArray.map((item, index) => ({
      label: startCase(item?.name),
      key: 6 + index,
      value: item?.type
    }));
  })();

  const handleClose = () => {
    setOpen(false)
    setIsEdit(false)
  } 

  const getHeaders = useCallback((value) => {
    const data = value.map((item) => {
      if(item?.key === "name" && customFields?.field_alias?.field_alias_name) {
        return {
          key: item?.key,
          label: startCase(customFields?.field_alias?.field_alias_name)
        }
      } else {
        return {
          ...item
        }
      }
    })
    return data
  }, [customFields?.field_alias?.field_alias_name])

  const projectInfo = {
    projectNumber: currentProject?.project_number || 'N/A',
    projectName: currentProject?.name || 'Project Name',
    reportTitle: current?.name || 'Delivery Report',
    reportDate: new Date().toLocaleDateString()
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      await exportToExcel(downLoadData?.deliveries, projectInfo, getHeaders(headers), downLoadData?.laydowns, partList)
      setSnackbar({ open: true, message: 'Downloaded Delivery report', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: 'Error downloading Excel.' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box>
      {loader ? (
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
           <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
              <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
          <Typography fontSize={20} mb={0.5} fontWeight={600}>
            {startCase(title)}
          </Typography>
          <Box display={"flex"} justifyContent={"flex-end"} gap={2} mb={1}>
          <Button
              variant="outlined"
              startIcon={<Download size={16} />}
              onClick={handleDownload}
              loading={loading}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cog size={16} />}
              onClick={() => {
                setEditData(null);  
                setTemp(true);
              }}
            >
              Setup Form
            </Button>
            <Button
              variant="outlined"
              startIcon={<Plus size={16} />}
              onClick={() => {
                setEditData(null);  
                setOpen(true);
                setIsEdit(true)
              }}
            >
              Add Delivery
            </Button>

          </Box>
          <TableComponent 
            data={data} 
            handleEdit={handleEdit} 
            aliasName={aliasName} 
            handleDelete={handleDelete}
            loader={loader}
        />
        </Box>
      )}
      <EditDeliveryModal
        open={open}
        onClose={handleClose}
        data={editData}
        partList={partList} 
        laydownList={laydownList}
        handleFetch={handleFetch}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        customFields={customFields}
        aliasName={aliasName}
      />
      <DeleteDialog
        open={visible}
        setOpen={setVisible}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        handleFetch={handleFetch}
        text={text}
      />
      <SetupDeliveryDialog 
        open={temp}
        onClose={onClose}
        defaultFormValues={defaultFormValues}
        newColumns={result}
        aliase={customFields?.field_alias?.field_alias_name}
        customFields={(() => {
          if (customFields?.custom_inputs && Array.isArray(customFields.custom_inputs)) {
            return customFields.custom_inputs;
          } else if (Array.isArray(customFields)) {
            return customFields
              .map(item => Array.isArray(item) ? item[0] : item)
              .filter(field => field && field.name);
          } else if (customFields?.custom_response && Array.isArray(customFields.custom_response)) {
            return customFields.custom_response
              .map(item => Array.isArray(item) ? item[0] : item)
              .filter(field => field && field.name);
          }
          return [];
        })()}
        setFields={setFields}
        handleFetch={handleFetch}
      />
    </Box>
  );
};

export default DeliveryManagement;